import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BookOpen, Heart } from "lucide-react";
import KineticHeading from "@/components/KineticHeading";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";

const heroImage = "/welcome-portrait.webp";

export default function Welcome() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const imageRotate = useTransform(scrollYProgress, [0, 1], [0, -5]);

  return (
    <div data-testid="welcome-page">
      <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden px-5 pb-20 pt-32 md:px-8" data-testid="welcome-hero">
        <div className="soft-orb left-[-8rem] top-24 h-80 w-80 bg-petal/70" />
        <div className="soft-orb bottom-16 right-[-7rem] h-96 w-96 bg-sky/80" />
        <div className="mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative z-10">
            <Reveal delay={0.05}>
              <p className="mb-8 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.35em] text-cocoa/55" data-testid="welcome-eyebrow">
                <Heart size={14} fill="currentColor" /> Romance for quiet hours
              </p>
            </Reveal>
            <KineticHeading testId="welcome-kinetic-heading" lines={["Morgan Ella", "books for", "the girls who", "love some", "romance."]} className="font-display text-[17vw] font-light leading-[0.78] tracking-[-0.06em] text-cocoa sm:text-8xl lg:text-[7.8rem]" />
            <Reveal delay={0.8} className="mt-10 max-w-xl">
              <p className="text-base leading-8 text-cocoa/68 md:text-lg" data-testid="welcome-intro-text">
                A cozy literary corner for the girls who love men that yearn, strong female protagonists, and moments that make you want to kick your feet giggling.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/books" data-testid="welcome-browse-books-button" className="group inline-flex items-center gap-3 rounded-full bg-cocoa px-7 py-4 text-sm text-white transition-transform duration-300 hover:scale-105">
                  Browse the collection <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/about" data-testid="welcome-manifesto-button" className="rounded-full border border-cocoa/15 px-7 py-4 text-sm transition-colors duration-300 hover:border-cocoa hover:bg-petal/50">
                  Read the manifesto
                </Link>
              </div>
            </Reveal>
          </div>
          <motion.div style={{ y: imageY, rotate: imageRotate }} className="relative mt-12 block lg:mt-0" data-testid="welcome-parallax-visual">
            <div className="spotlight absolute inset-8 rounded-full bg-petal" />
            <div className="hero-frame relative aspect-[4/5] overflow-hidden">
              <img src={heroImage} alt="Morgan holding her book Almost, Always" data-testid="welcome-hero-image" className="h-full w-full object-cover object-center" />
            </div>
            <div className="absolute -left-4 bottom-10 rounded-full border border-cocoa/10 bg-white/80 px-6 py-4 shadow-xl backdrop-blur-md md:-left-8">
              <p className="font-display text-2xl italic">Chapter one begins here</p>
            </div>
          </motion.div>
        </div>
      </section>
      <Marquee />
      <section className="px-5 py-28 md:px-8 md:py-36" data-testid="welcome-notes-section">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
          {[
            ["01", "Almost, Always", "Brother's best friend and second chance romance. “Can love really last a lifetime?”", "bg-sage"],
            ["02", "Book 2 Coming Soon", "Coming soon", "bg-petal"],
            ["03", "Book 3 Coming Soon", "Coming soon", "bg-lavender"],
          ].map(([number, title, text, color], index) => (
            <Reveal key={number} delay={index * 0.08}>
              <div className={`${color} min-h-72 rounded-[2.5rem] border border-cocoa/10 p-8`} data-testid={`welcome-note-card-${number}`}>
                <BookOpen size={22} className="text-cocoa/50" />
                <p className="mt-12 font-display text-6xl italic text-cocoa/25">{number}</p>
                <h2 className="mt-4 font-display text-4xl">{title}</h2>
                <p className="mt-4 text-sm leading-7 text-cocoa/65">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
